// List selection primitives shared by every list that supports click + Shift+click:
// the document tree/list and the file email tab. They work on plain string ids and a
// visible order, so they stay free of component/Fluent imports (jest/ESM constraint).

export type SelectionResult = {
    next: Set<string>
    // The row that becomes the anchor for a subsequent Shift+click range.
    anchor?: string
}

export const toggleSelect = (selected: ReadonlySet<string>, id: string): SelectionResult => {
    const next = new Set(selected)
    if (next.has(id)) {
        next.delete(id)
    } else {
        next.add(id)
    }
    return { next, anchor: id }
}

// Shift+click: add the range between the anchor and the target to the selection. Rows
// outside that range keep their state. `skip` lets a caller drop ids from the range
// (e.g. folders in the task attachment picker). Without a usable anchor — nothing
// selected yet, or the anchor scrolled out of the loaded order — it falls back to a
// plain toggle.
export const selectRangeByOrder = (
    visibleOrder: string[],
    anchorId: string | undefined,
    targetId: string,
    selected: ReadonlySet<string>,
    skip?: (id: string) => boolean,
): SelectionResult => {
    const anchorIndex = anchorId ? visibleOrder.indexOf(anchorId) : -1
    const targetIndex = visibleOrder.indexOf(targetId)
    if (anchorIndex === -1 || targetIndex === -1) {
        return toggleSelect(selected, targetId)
    }
    const [from, to] = anchorIndex < targetIndex ? [anchorIndex, targetIndex] : [targetIndex, anchorIndex]
    const next = new Set(selected)
    for (const id of visibleOrder.slice(from, to + 1)) {
        if (skip?.(id)) {
            continue
        }
        next.add(id)
    }
    // The anchor stays put so a follow-up Shift+click extends from the same origin.
    return { next, anchor: anchorId }
}
